'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getFraudCvvAttemptCount, validatePaymentCvv } from '@libs/api-proxy-client';
import { logSentryError, SentryAcpCreditCardsErrors as SentryErrors, useDecisions } from '@libs/common-next';
import { MAX_CVV_ATTEMPTS } from './constants';
export interface CvvValidationOutcome {
  isValid: boolean;
  remainingAttempts: number;
}
export interface CVVChallengeContextValue {
  /** The customer's failed CVV attempts. Undefined until the count loads. */
  attemptCount?: number;
  isLoading: boolean;
  /** The customer is out of CVV attempts and has to go through full card validation. */
  isLimitReached: boolean;
  /** Checks the CVV and keeps the attempt count in sync with the response. */
  validateCvv: (cardId: number, encryptedSecurityCode: string) => Promise<CvvValidationOutcome>;
  /** Loads the attempt count again, for example after full card validation. */
  refreshAttemptCount: () => Promise<void>;
}
const CVVChallengeContext = createContext<CVVChallengeContextValue | null>(null);
async function fetchAttemptCount(): Promise<number> {
  try {
    const response = await getFraudCvvAttemptCount();
    return response?.fraudCVVAttemptCount ?? 0;
  } catch (error) {
    // The validate endpoint still enforces the limit, so fall back to the CVV check.
    logSentryError(error, SentryErrors.CVVChallenge.FetchAttemptCountFailed);
    return 0;
  }
}

/** Keeps the customer's failed CVV attempts so any component below can tell whether a CVV check is still possible. */
export function CVVChallengeProvider(t0) {
  const $ = _c(12);
  const {
    children
  } = t0;
  const [attemptCount, setAttemptCount] = useState();
  const enabled = useDecisions("as_cvv_validate");
  let t1;
  let t2;
  if ($[0] !== enabled) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      let isCancelled = false;
      fetchAttemptCount().then(count => {
        if (!isCancelled) {
          setAttemptCount(count);
        }
      });
      return () => {
        isCancelled = true;
      };
    };
    t2 = [enabled];
    $[0] = enabled;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async () => {
      setAttemptCount(await fetchAttemptCount());
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const refreshAttemptCount = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = async (cardId, encryptedSecurityCode) => {
      const result = await validatePaymentCvv(cardId, encryptedSecurityCode);
      const isValid = Boolean(result?.validated);
      const count_0 = result?.fraudCVVAttemptCount ?? (isValid ? 0 : MAX_CVV_ATTEMPTS);
      setAttemptCount(count_0);
      return {
        isValid,
        remainingAttempts: Math.max(MAX_CVV_ATTEMPTS - count_0, 0)
      };
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const validateCvv = t4;
  const t5 = enabled && attemptCount === undefined;
  const t6 = attemptCount !== undefined && attemptCount >= MAX_CVV_ATTEMPTS;
  let t7;
  if ($[5] !== attemptCount || $[6] !== t5 || $[7] !== t6) {
    t7 = {
      attemptCount,
      isLoading: t5,
      isLimitReached: t6,
      validateCvv,
      refreshAttemptCount
    };
    $[5] = attemptCount;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const value = t7;
  let t8;
  if ($[9] !== children || $[10] !== value) {
    t8 = <CVVChallengeContext.Provider value={value}>{children}</CVVChallengeContext.Provider>;
    $[9] = children;
    $[10] = value;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
}
export function useCvvChallenge() {
  const context = useContext(CVVChallengeContext);
  if (!context) {
    throw new Error("useCvvChallenge must be used within a CVVChallengeProvider");
  }
  return context;
}